import { PAGINATION_TIME_IN_MILLISECONDS } from '@shared/constants';
import { Group, Tween } from '@tweenjs/tween.js';
import { RefObject, useEffect, useRef, useState } from 'react';
import useAnimationControls from './useAnimationControls';

function getScrollTopMax(scrollElement: HTMLDivElement): number {
  return scrollElement.scrollHeight - scrollElement.clientHeight;
}

export default function useLinearScroll(
  scrollElementRef: RefObject<HTMLDivElement | null>,
  scrollTimeInMilliseconds: number | undefined,
  delayInMilliseconds: number | undefined,
): void {
  const [scrollTopMax, setScrollTopMax] = useState(0);
  const { isPaused } = useAnimationControls();
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;

  useEffect(() => {
    const scrollElement = scrollElementRef.current;
    if (!scrollElement) {
      return;
    }

    function onResize() {
      if (!scrollElement) {
        return;
      }
      setScrollTopMax(getScrollTopMax(scrollElement));
    }

    onResize();
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(scrollElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollElementRef]);

  useEffect(() => {
    if (!scrollTimeInMilliseconds) {
      return;
    }

    const scrollElement = scrollElementRef.current;
    if (!scrollElement) {
      return;
    }

    if (scrollTopMax <= 0) {
      return;
    }

    const delay = delayInMilliseconds ?? 0;

    const position = { scrollTop: scrollElement.scrollTop };
    const scrollDown = new Tween(position)
      .delay(delay)
      .to({ scrollTop: scrollTopMax }, scrollTimeInMilliseconds)
      .onUpdate(() => {
        scrollElement.scrollTop = position.scrollTop;
      });

    const scrollUp = new Tween(position)
      .delay(delay + PAGINATION_TIME_IN_MILLISECONDS) // Wait to avoid scroll during eventual pagination
      .to({ scrollTop: 0 }, 1000)
      .onUpdate(() => {
        scrollElement.scrollTop = position.scrollTop;
      });

    scrollDown.chain(scrollUp);
    scrollUp.chain(scrollDown);
    scrollDown.start();

    const group = new Group(scrollDown, scrollUp);

    let animating = true;
    let pauseStartTime: number | null = null;
    let totalPausedTime = 0;
    function animate(time: number) {
      if (!animating) {
        return;
      }
      if (isPausedRef.current) {
        if (pauseStartTime === null) {
          pauseStartTime = time;
        }
        requestAnimationFrame(animate);
        return;
      }
      if (pauseStartTime !== null) {
        totalPausedTime += time - pauseStartTime;
        pauseStartTime = null;
      }
      group.update(time - totalPausedTime);
      requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);

    return () => {
      animating = false;
    };
  }, [
    delayInMilliseconds,
    scrollElementRef,
    scrollTimeInMilliseconds,
    scrollTopMax,
  ]);
}
